import React, { useState, useEffect } from 'react';
import { LineChart, Line, ScatterChart, Scatter, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getChatCompletion } from '../../utils/openaiClient';

const ArtemisDecisionPlatform = () => {
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [exampleType, setExampleType] = useState('software'); // 'software', 'ml', 'trading', 'data'
  const [preferences, setPreferences] = useState({
    performance: 0.4,
    memory: 0.3,
    readability: 0.2,
    security: 0.1
  });
  const [mlPreferences, setMlPreferences] = useState({
    accuracy: 0.35,
    interpretability: 0.25,
    trainingSpeed: 0.2,
    inferenceSpeed: 0.2
  });
  const [tradingPreferences, setTradingPreferences] = useState({
    returns: 0.35,
    risk: 0.3,
    latency: 0.2,
    complexity: 0.15
  });
  const [dataPreferences, setDataPreferences] = useState({
    throughput: 0.3,
    reliability: 0.3,
    cost: 0.2,
    maintainability: 0.2
  });
  const [autoRecommended, setAutoRecommended] = useState(2);
  const [viewMode, setViewMode] = useState('overview');
  const [selectedAgentOutput, setSelectedAgentOutput] = useState(null);
  const [humanDecision, setHumanDecision] = useState('');
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [isProcessingNL, setIsProcessingNL] = useState(false);
  const [userDecisions, setUserDecisions] = useState([]);
  const [hasSetInitialPreferences, setHasSetInitialPreferences] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);

  // Task descriptions for each example type
  const taskDescriptions = {
    software: "Optimize a data processing function that handles millions of records",
    ml: "Build a customer churn prediction model for a SaaS company",
    trading: "Create a high-frequency trading strategy for cryptocurrency arbitrage",
    data: "Design a real-time data pipeline for IoT sensor data processing"
  };

  // User persona examples
  const userPersonas = {
    software: [
      { name: "Startup CTO", preferences: { performance: 0.5, memory: 0.1, readability: 0.3, security: 0.1 }, description: "Need fast MVP, team will grow" },
      { name: "Enterprise Architect", preferences: { performance: 0.2, memory: 0.2, readability: 0.3, security: 0.3 }, description: "Large team, compliance focus" },
      { name: "Solo Developer", preferences: { performance: 0.3, memory: 0.2, readability: 0.4, security: 0.1 }, description: "I maintain everything myself" }
    ],
    ml: [
      { name: "Data Scientist", preferences: { accuracy: 0.6, interpretability: 0.2, trainingSpeed: 0.1, inferenceSpeed: 0.1 }, description: "Research focus, best model wins" },
      { name: "ML Engineer", preferences: { accuracy: 0.3, interpretability: 0.1, trainingSpeed: 0.2, inferenceSpeed: 0.4 }, description: "Production deployment matters" },
      { name: "Business Analyst", preferences: { accuracy: 0.3, interpretability: 0.5, trainingSpeed: 0.1, inferenceSpeed: 0.1 }, description: "Need to explain to stakeholders" }
    ],
    trading: [
      { name: "Hedge Fund", preferences: { returns: 0.5, risk: 0.3, latency: 0.1, complexity: 0.1 }, description: "Alpha is everything" },
      { name: "Retail Trader", preferences: { returns: 0.3, risk: 0.4, latency: 0.1, complexity: 0.2 }, description: "Can't afford big losses" },
      { name: "HFT Firm", preferences: { returns: 0.2, risk: 0.2, latency: 0.5, complexity: 0.1 }, description: "Speed wins arbitrage" }
    ],
    data: [
      { name: "Startup", preferences: { throughput: 0.2, reliability: 0.3, cost: 0.4, maintainability: 0.1 }, description: "Bootstrap budget" },
      { name: "Enterprise", preferences: { throughput: 0.3, reliability: 0.4, cost: 0.1, maintainability: 0.2 }, description: "Can't have downtime" },
      { name: "Scale-up", preferences: { throughput: 0.4, reliability: 0.3, cost: 0.2, maintainability: 0.1 }, description: "Growing fast" }
    ]
  };

  // Software engineering code variations
  const softwareVariations = [
    {
      id: 1,
      name: "Ultra-Fast Variant",
      agent: "GPT Agent",
      agentIcon: "⚡",
      description: "Optimized for maximum performance",
      scores: { performance: 95, memory: 60, readability: 70, security: 85 },
      color: "#FF6B6B",
      branch: "perf/parallel-processing",
      commitMessage: "feat: implement parallel processing with thread pools",
      codeSnippet: "// Parallel processing with thread pools\nconst results = await Promise.all(\n  data.map(item => processAsync(item))\n);",
      reasoning: "Uses parallel processing to maximize CPU utilization",
      tradeoffs: "Higher memory usage, moderate complexity"
    },
    {
      id: 2,
      name: "Balanced Solution",
      agent: "Claude Agent",
      agentIcon: "⚖️",
      description: "Best overall trade-offs",
      scores: { performance: 85, memory: 80, readability: 90, security: 88 },
      color: "#4ECDC4",
      branch: "feat/smart-caching",
      commitMessage: "feat: add intelligent caching layer",
      codeSnippet: "// Optimized with caching\nconst cached = cache.get(key);\nif (!cached) {\n  const result = await process(data);\n  cache.set(key, result);\n}",
      reasoning: "Balances all metrics with intelligent caching",
      tradeoffs: "Slightly lower peak performance for better overall quality"
    },
    {
      id: 3,
      name: "Memory Efficient",
      agent: "Gemini Agent",
      agentIcon: "💾",
      description: "Minimal memory footprint",
      scores: { performance: 75, memory: 95, readability: 85, security: 82 },
      color: "#45B7D1",
      branch: "opt/streaming-approach",
      commitMessage: "refactor: implement streaming to reduce memory",
      codeSnippet: "// Streaming approach\nfor await (const chunk of stream) {\n  processChunk(chunk);\n  releaseMemory();\n}",
      reasoning: "Streaming prevents memory accumulation",
      tradeoffs: "Sequential processing limits parallelism"
    },
    {
      id: 4,
      name: "Clean Code Focus",
      agent: "GPT Agent",
      agentIcon: "✨",
      description: "Maximum maintainability",
      scores: { performance: 70, memory: 75, readability: 98, security: 90 },
      color: "#96CEB4",
      branch: "refactor/clean-architecture",
      commitMessage: "refactor: apply clean code principles",
      codeSnippet: "// Clean, self-documenting code\nconst processUserData = async (userData) => {\n  const validated = validateInput(userData);\n  const processed = transformData(validated);\n  return sanitizeOutput(processed);\n}",
      reasoning: "Prioritizes code clarity and maintainability",
      tradeoffs: "Some performance overhead for better structure"
    },
    {
      id: 5,
      name: "Security Hardened",
      agent: "Llama Agent",
      agentIcon: "🛡️",
      description: "Maximum security measures",
      scores: { performance: 78, memory: 70, readability: 80, security: 98 },
      color: "#DDA0DD",
      branch: "security/encryption-layer",
      commitMessage: "security: add encryption and validation layers",
      codeSnippet: "// Security-first approach\nconst secureProcess = async (data) => {\n  validatePermissions();\n  const encrypted = await encrypt(data);\n  return processSecurely(encrypted);\n}",
      reasoning: "Multiple security layers and encryption",
      tradeoffs: "Additional processing for security checks"
    },
    {
      id: 6,
      name: "Evolution Optimized",
      agent: "Artemis Agent",
      agentIcon: "🚀",
      description: "AI-evolved optimization",
      scores: { performance: 88, memory: 82, readability: 86, security: 84 },
      color: "#F4511E",
      branch: "artemis/evolved-solution",
      commitMessage: "feat: artemis-evolved multi-objective optimization",
      codeSnippet: "// Artemis-evolved balanced solution\nconst processOptimized = async (data) => {\n  const chunks = await smartChunking(data);\n  const results = await Promise.allSettled(\n    chunks.map(c => processWithRetry(c))\n  );\n  return mergeResults(results);\n}",
      reasoning: "Evolved through multi-objective optimization",
      tradeoffs: "Balanced approach optimized by AI"
    }
  ];

  // ML-specific code variations
  const mlVariations = [
    {
      id: 1,
      name: "Deep Neural Network",
      agent: "GPT Agent",
      agentIcon: "🧠",
      description: "Complex model for highest accuracy",
      scores: { accuracy: 98, interpretability: 30, trainingSpeed: 40, inferenceSpeed: 50 },
      color: "#FF6B6B",
      branch: "model/deep-learning",
      commitMessage: "feat: implement LSTM with attention mechanism",
      codeSnippet: `# Deep learning model with attention
model = tf.keras.Sequential([
    tf.keras.layers.LSTM(256, return_sequences=True),
    tf.keras.layers.MultiHeadAttention(num_heads=8, key_dim=64),
    tf.keras.layers.Dense(128, activation='relu'),
    tf.keras.layers.Dropout(0.3),
    tf.keras.layers.Dense(num_classes, activation='softmax')
])`,
      reasoning: "Deep architecture captures complex patterns",
      tradeoffs: "Black box model, slow training, requires GPU"
    },
    {
      id: 2,
      name: "Gradient Boosting",
      agent: "Claude Agent",
      agentIcon: "🌲",
      description: "Good accuracy with interpretability",
      scores: { accuracy: 92, interpretability: 75, trainingSpeed: 70, inferenceSpeed: 80 },
      color: "#4ECDC4",
      branch: "model/xgboost",
      commitMessage: "feat: implement XGBoost with feature importance",
      codeSnippet: `# XGBoost with feature importance
model = xgb.XGBClassifier(
    n_estimators=100,
    max_depth=6,
    learning_rate=0.1,
    objective='multi:softprob'
)
model.fit(X_train, y_train)
# Get feature importance
importance = model.feature_importances_`,
      reasoning: "Tree-based model provides feature insights",
      tradeoffs: "Slightly lower accuracy than deep learning"
    },
    {
      id: 3,
      name: "Linear Model + Features",
      agent: "Gemini Agent",
      agentIcon: "📊",
      description: "Maximum interpretability",
      scores: { accuracy: 85, interpretability: 98, trainingSpeed: 95, inferenceSpeed: 98 },
      color: "#45B7D1",
      branch: "model/logistic-regression",
      commitMessage: "feat: implement interpretable linear model",
      codeSnippet: `# Logistic regression with engineered features
poly_features = PolynomialFeatures(degree=2)
X_poly = poly_features.fit_transform(X)

model = LogisticRegression(
    penalty='l2',
    solver='lbfgs',
    multi_class='multinomial'
)
model.fit(X_poly, y)
# Coefficients directly interpretable`,
      reasoning: "Linear model with clear feature contributions",
      tradeoffs: "Lower accuracy on complex patterns"
    },
    {
      id: 4,
      name: "AutoML Pipeline",
      agent: "GPT Agent",
      agentIcon: "🤖",
      description: "Automated optimization",
      scores: { accuracy: 90, interpretability: 60, trainingSpeed: 60, inferenceSpeed: 85 },
      color: "#96CEB4",
      branch: "model/automl",
      commitMessage: "feat: add AutoML pipeline with TPOT",
      codeSnippet: `# AutoML with TPOT
from tpot import TPOTClassifier

model = TPOTClassifier(
    generations=5,
    population_size=20,
    cv=5,
    random_state=42,
    verbosity=2
)
model.fit(X_train, y_train)
# Exports optimized pipeline`,
      reasoning: "Automatically finds best model architecture",
      tradeoffs: "Less control over model specifics"
    },
    {
      id: 5,
      name: "Ensemble with SHAP",
      agent: "Llama Agent",
      agentIcon: "🔀",
      description: "Accuracy + explainability",
      scores: { accuracy: 94, interpretability: 85, trainingSpeed: 55, inferenceSpeed: 65 },
      color: "#DDA0DD",
      branch: "model/ensemble-shap",
      commitMessage: "feat: create ensemble with SHAP explanations",
      codeSnippet: `# Ensemble with SHAP explanations
ensemble = VotingClassifier([
    ('rf', RandomForestClassifier(n_estimators=100)),
    ('gb', GradientBoostingClassifier()),
    ('svm', SVC(probability=True))
])
ensemble.fit(X_train, y_train)

# SHAP for interpretability
explainer = shap.Explainer(ensemble.predict, X_train)
shap_values = explainer(X_test)`,
      reasoning: "Combines multiple models with post-hoc explanations",
      tradeoffs: "Computational overhead for explanations"
    },
    {
      id: 6,
      name: "Artemis Evolved ML",
      agent: "Artemis Agent",
      agentIcon: "🚀",
      description: "Multi-objective optimized",
      scores: { accuracy: 91, interpretability: 82, trainingSpeed: 78, inferenceSpeed: 75 },
      color: "#F4511E",
      branch: "model/artemis-evolved",
      commitMessage: "feat: artemis-evolved multi-objective ML pipeline",
      codeSnippet: `# Artemis-evolved balanced ML solution
model = ArtemisEvolvedPipeline([
    ('preprocessor', SmartPreprocessor()),
    ('feature_selector', AdaptiveSelector()),
    ('model', BalancedEnsemble([
        ('fast', LightGBM()),
        ('accurate', XGBoost()),
        ('interpretable', LinearSVM())
    ])),
    ('explainer', IntegratedExplainer())
])`,
      reasoning: "Balanced all objectives through evolution",
      tradeoffs: "Optimized for overall performance"
    }
  ];

  // Algorithmic trading variations
  const tradingVariations = [
    {
      id: 1,
      name: "Ultra Low Latency",
      agent: "GPT Agent",
      agentIcon: "🏎️",
      description: "Microsecond execution focus",
      scores: { returns: 82, risk: 60, latency: 99, complexity: 50 },
      color: "#FF6B6B",
      branch: "strategy/hft-cpp",
      commitMessage: "perf: implement lock-free HFT engine",
      codeSnippet: `// C++ HFT implementation
class ArbitrageEngine {
    alignas(64) std::atomic<double> prices[2];
    void execute_trade() noexcept {
        if (prices[0] - prices[1] > threshold) {
            order_manager.send_immediate(BUY, exchange_1);
            order_manager.send_immediate(SELL, exchange_2);
        }
    }
};`,
      reasoning: "Hardware-optimized, lock-free data structures",
      tradeoffs: "Complex implementation, high infrastructure cost"
    },
    {
      id: 2,
      name: "Risk-Adjusted Returns",
      agent: "Claude Agent",
      agentIcon: "📈",
      description: "Sharpe ratio optimization",
      scores: { returns: 88, risk: 92, latency: 75, complexity: 70 },
      color: "#4ECDC4",
      branch: "strategy/kelly-criterion",
      commitMessage: "feat: add Kelly criterion position sizing",
      codeSnippet: `# Portfolio optimization strategy
def calculate_signals(self, market_data):
    # Kelly criterion position sizing
    edge = self.expected_return - self.risk_free_rate
    odds = self.expected_return / self.expected_loss
    kelly_fraction = edge / odds
    
    # Dynamic hedging
    hedge_ratio = self.calculate_beta(market_data)
    position_size = kelly_fraction * self.capital * (1 - hedge_ratio)
    
    return self.generate_orders(position_size)`,
      reasoning: "Mathematical optimization of risk/reward",
      tradeoffs: "Moderate latency, requires accurate predictions"
    },
    {
      id: 3,
      name: "ML-Powered Signals",
      agent: "Gemini Agent",
      agentIcon: "🤖",
      description: "Deep learning predictions",
      scores: { returns: 91, risk: 70, latency: 60, complexity: 85 },
      color: "#45B7D1",
      branch: "strategy/lstm-signals",
      commitMessage: "ml: add LSTM price prediction model",
      codeSnippet: `# LSTM-based price prediction
model = Sequential([
    LSTM(128, return_sequences=True),
    Attention(256),
    Dense(64, activation='relu'),
    Dropout(0.3),
    Dense(3, activation='softmax')  # Buy/Hold/Sell
])

# Real-time inference
signal = model.predict(
    preprocessed_market_data[-lookback:]
)`,
      reasoning: "Captures complex market patterns",
      tradeoffs: "Black box decisions, model risk"
    },
    {
      id: 4,
      name: "Statistical Arbitrage",
      agent: "GPT Agent",
      agentIcon: "📊",
      description: "Pairs trading strategy",
      scores: { returns: 85, risk: 88, latency: 85, complexity: 65 },
      color: "#96CEB4",
      branch: "strategy/pairs-trading",
      commitMessage: "feat: implement cointegration-based pairs",
      codeSnippet: `# Cointegration-based pairs trading
def find_arbitrage_opportunities(self):
    # Calculate z-score of spread
    spread = self.pair_prices[0] - self.beta * self.pair_prices[1]
    z_score = (spread - self.spread_mean) / self.spread_std
    
    if z_score > 2.0:  # Sell spread
        self.short(self.pair[0], size)
        self.long(self.pair[1], size * self.beta)
    elif z_score < -2.0:  # Buy spread
        self.long(self.pair[0], size)
        self.short(self.pair[1], size * self.beta)`,
      reasoning: "Statistically proven edge",
      tradeoffs: "Limited opportunities, requires monitoring"
    },
    {
      id: 5,
      name: "Multi-Exchange Smart",
      agent: "Llama Agent",
      agentIcon: "🌐",
      description: "Cross-exchange optimization",
      scores: { returns: 87, risk: 75, latency: 70, complexity: 80 },
      color: "#DDA0DD",
      branch: "strategy/smart-routing",
      commitMessage: "feat: add multi-exchange smart order routing",
      codeSnippet: `# Smart order routing
async def execute_best_price(order):
    # Query all exchanges simultaneously
    prices = await asyncio.gather(*[
        exchange.get_quote(order.symbol)
        for exchange in self.exchanges
    ])
    
    # Smart routing logic
    best_venue = self.calculate_optimal_route(
        prices, order.size, self.fee_structure
    )
    
    # Execute with anti-slippage protection
    await self.execute_with_limits(best_venue, order)`,
      reasoning: "Finds best execution across venues",
      tradeoffs: "Network complexity, regulatory compliance"
    }
  ];

  // Data pipeline variations
  const dataVariations = [
    {
      id: 1,
      name: "Stream Processing",
      agent: "GPT Agent",
      agentIcon: "🌊",
      description: "Apache Kafka + Flink pipeline",
      scores: { throughput: 95, reliability: 80, cost: 60, maintainability: 70 },
      color: "#FF6B6B",
      branch: "pipeline/kafka-flink",
      commitMessage: "feat: implement Kafka+Flink streaming",
      codeSnippet: `// Apache Flink streaming job
DataStream<SensorReading> stream = env
    .addSource(new FlinkKafkaConsumer<>(
        "iot-sensors",
        new SensorSchema(),
        kafkaProps))
    .keyBy(r -> r.sensorId)
    .window(TumblingEventTimeWindows.of(Time.seconds(10)))
    .aggregate(new SensorAggregator())
    .addSink(new ElasticsearchSink<>());`,
      reasoning: "Low latency, handles millions of events/sec",
      tradeoffs: "Complex deployment, high resource usage"
    },
    {
      id: 2,
      name: "Batch + Stream Hybrid",
      agent: "Claude Agent",
      agentIcon: "🔄",
      description: "Lambda architecture",
      scores: { throughput: 88, reliability: 92, cost: 75, maintainability: 80 },
      color: "#4ECDC4",
      branch: "pipeline/lambda-arch",
      commitMessage: "feat: implement lambda architecture",
      codeSnippet: `# PySpark hybrid pipeline
# Speed layer - streaming
stream_df = spark.readStream \
    .format("kafka") \
    .option("subscribe", "iot-sensors") \
    .load() \
    .select(from_json(col("value"), schema).alias("data"))

# Batch layer - historical
batch_df = spark.read.parquet("s3://data-lake/sensors/")

# Serving layer
merged_view = stream_df.union(batch_df) \
    .groupBy(window("timestamp", "1 hour")) \
    .agg(avg("temperature"), max("humidity"))`,
      reasoning: "Balances real-time and accuracy needs",
      tradeoffs: "Dual pipeline complexity"
    },
    {
      id: 3,
      name: "Serverless Pipeline",
      agent: "Gemini Agent",
      agentIcon: "☁️",
      description: "AWS Lambda + Kinesis",
      scores: { throughput: 75, reliability: 85, cost: 95, maintainability: 90 },
      color: "#45B7D1",
      branch: "pipeline/serverless",
      commitMessage: "feat: serverless data processing pipeline",
      codeSnippet: `# Serverless data pipeline
def lambda_handler(event, context):
    # Process Kinesis records
    for record in event['Records']:
        payload = json.loads(
            base64.b64decode(record['kinesis']['data'])
        )
        
        # Transform and enrich
        enriched = enrich_sensor_data(payload)
        
        # Write to DynamoDB
        table.put_item(Item=enriched)
        
        # Trigger downstream if threshold
        if enriched['temperature'] > THRESHOLD:
            sns.publish(TopicArn=ALERT_TOPIC, Message=json.dumps(enriched))`,
      reasoning: "Zero infrastructure management",
      tradeoffs: "Cold starts, vendor lock-in"
    },
    {
      id: 4,
      name: "Kubernetes Native",
      agent: "GPT Agent",
      agentIcon: "🐳",
      description: "Cloud-native microservices",
      scores: { throughput: 85, reliability: 88, cost: 70, maintainability: 85 },
      color: "#96CEB4",
      branch: "pipeline/k8s-native",
      commitMessage: "feat: kubernetes-native data pipeline",
      codeSnippet: `# Kubernetes deployment
apiVersion: apps/v1
kind: Deployment
metadata:
  name: sensor-processor
spec:
  replicas: 10
  template:
    spec:
      containers:
      - name: processor
        image: iot-processor:latest
        env:
        - name: KAFKA_BROKERS
          value: "kafka:9092"
        resources:
          requests:
            memory: "256Mi"
            cpu: "500m"
---
# Horizontal Pod Autoscaler
apiVersion: autoscaling/v2
kind: HorizontalPodAutoscaler
spec:
  minReplicas: 5
  maxReplicas: 50
  targetCPUUtilizationPercentage: 70`,
      reasoning: "Highly scalable and portable",
      tradeoffs: "Operational complexity"
    },
    {
      id: 5,
      name: "Edge + Cloud Hybrid",
      agent: "Llama Agent",
      agentIcon: "📡",
      description: "Edge computing with cloud sync",
      scores: { throughput: 82, reliability: 90, cost: 85, maintainability: 75 },
      color: "#DDA0DD",
      branch: "pipeline/edge-cloud",
      commitMessage: "feat: edge computing with cloud sync",
      codeSnippet: `# Edge device processing
class EdgeProcessor:
    def __init__(self):
        self.local_buffer = []
        self.model = load_edge_model()
    
    def process_locally(self, sensor_data):
        # Real-time inference at edge
        prediction = self.model.predict(sensor_data)
        
        # Local aggregation
        self.local_buffer.append({
            'timestamp': time.time(),
            'raw': sensor_data,
            'prediction': prediction
        })
        
        # Batch upload to cloud
        if len(self.local_buffer) >= BATCH_SIZE:
            self.sync_to_cloud()
            
    def sync_to_cloud(self):
        compressed = gzip.compress(
            json.dumps(self.local_buffer).encode()
        )
        cloud_api.upload(compressed)`,
      reasoning: "Reduces bandwidth, enables offline operation",
      tradeoffs: "Edge device management complexity"
    }
  ];

  // Get current variations and preferences based on example type
  const getVariationsAndPreferences = () => {
    switch(exampleType) {
      case 'software':
        return {
          variations: softwareVariations,
          preferences: preferences,
          setPreferences: setPreferences
        };
      case 'ml':
        return {
          variations: mlVariations,
          preferences: mlPreferences,
          setPreferences: setMlPreferences
        };
      case 'trading':
        return {
          variations: tradingVariations,
          preferences: tradingPreferences,
          setPreferences: setTradingPreferences
        };
      case 'data':
        return {
          variations: dataVariations,
          preferences: dataPreferences,
          setPreferences: setDataPreferences
        };
      default:
        return {
          variations: softwareVariations,
          preferences: preferences,
          setPreferences: setPreferences
        };
    }
  };

  const { variations: currentVariations, preferences: currentPreferences, setPreferences: setCurrentPreferences } = getVariationsAndPreferences();

  // Calculate weighted score for each variation
  const calculateWeightedScore = (variation) => {
    const scores = variation.scores;
    const prefs = currentPreferences;
    return Object.keys(scores).reduce((total, key) => {
      return total + (scores[key] * (prefs[key] || 0));
    }, 0);
  };

  // Sort variations by weighted score
  const sortedVariations = [...currentVariations].sort((a, b) => 
    calculateWeightedScore(b) - calculateWeightedScore(a)
  );

  // Calculate performance ranking
  const getPerformanceRank = (variation) => {
    const rank = sortedVariations.findIndex(v => v.id === variation.id) + 1;
    const score = calculateWeightedScore(variation);
    const topScore = calculateWeightedScore(sortedVariations[0]);
    const percentOfBest = ((score / topScore) * 100).toFixed(0);
    
    return { rank, score, percentOfBest };
  };

  // Process natural language preference
  const processNaturalLanguage = async () => {
    if (!naturalLanguageInput.trim()) return;
    
    setIsProcessingNL(true);
    try {
      const metricsMap = {
        software: "performance, memory, readability, security",
        ml: "accuracy, interpretability, trainingSpeed, inferenceSpeed",
        trading: "returns, risk, latency, complexity",
        data: "throughput, reliability, cost, maintainability"
      };
      
      const systemPrompt = `You are a preference parser. Convert natural language preferences into weights for these metrics: ${metricsMap[exampleType]}. 
        Return ONLY a JSON object with these keys and decimal values that sum to 1.0.`;

      const response = await getChatCompletion(naturalLanguageInput, {
        systemPrompt,
        temperature: 0.3,
        maxTokens: 150
      });

      // Parse the JSON response
      const weights = JSON.parse(response);
      
      // Normalize to ensure sum is 1
      const sum = Object.values(weights).reduce((a, b) => a + b, 0);
      const normalized = {};
      Object.keys(weights).forEach(key => {
        normalized[key] = weights[key] / sum;
      });
      
      setCurrentPreferences(normalized);
      setHasSetInitialPreferences(true);
      
      // Add to user decisions history
      setUserDecisions(prev => [...prev, {
        type: 'preference_update',
        timestamp: new Date().toISOString(),
        input: naturalLanguageInput,
        result: normalized
      }]);
      
      setNaturalLanguageInput('');
    } catch (error) {
      console.error('Error processing natural language:', error);
      alert('Could not process your preference. Please try again with a clearer description.');
    } finally {
      setIsProcessingNL(false);
    }
  };

  // Update auto-recommendation based on preferences
  useEffect(() => {
    const topVariation = sortedVariations[0];
    setAutoRecommended(topVariation.id);
  }, [currentPreferences, exampleType]);

  // Score bar component
  const ScoreBar = ({ label, value, color, weight }) => (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginBottom: '2px',
        fontSize: '11px'
      }}>
        <span>{label}</span>
        <span>{value}% {weight && <span style={{ opacity: 0.6 }}>(×{(weight * 100).toFixed(0)}%)</span>}</span>
      </div>
      <div style={{
        width: '100%',
        height: '6px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '3px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${value}%`,
          height: '100%',
          background: color || '#4ECDC4',
          borderRadius: '3px',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  );

  // User intervention indicator
  const UserInterventionPoint = ({ step, description }) => (
    <div style={{
      background: 'rgba(255, 193, 7, 0.2)',
      border: '2px dashed #FFC107',
      borderRadius: '8px',
      padding: '12px',
      margin: '10px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    }}>
      <div style={{
        width: '30px',
        height: '30px',
        background: '#FFC107',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#000'
      }}>
        {step}
      </div>
      <div>
        <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '2px' }}>
          USER INTERVENTION
        </div>
        <div style={{ fontSize: '11px', opacity: 0.9 }}>
          {description}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      height: '100vh',
      background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
      display: 'flex',
      flexDirection: 'column',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 30px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #F4511E, #FF6D00)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              🚀 Artemis Decision Platform
            </h1>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
              The Future of Work: Multiple AI Agents Compete to Solve Your Task
            </p>
          </div>
          
          {/* Example Type Selector */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', maxWidth: '500px' }}>
            <button
              onClick={() => {
                setExampleType('software');
                setShowWelcome(true);
                setHasSetInitialPreferences(false);
              }}
              style={{
                padding: '8px 20px',
                background: exampleType === 'software' ? 
                  'linear-gradient(135deg, #F4511E, #FF6D00)' : 
                  'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '20px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              💻 Software
            </button>
            <button
              onClick={() => {
                setExampleType('ml');
                setShowWelcome(true);
                setHasSetInitialPreferences(false);
              }}
              style={{
                padding: '8px 20px',
                background: exampleType === 'ml' ? 
                  'linear-gradient(135deg, #F4511E, #FF6D00)' : 
                  'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '20px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🤖 ML
            </button>
            <button
              onClick={() => {
                setExampleType('trading');
                setShowWelcome(true);
                setHasSetInitialPreferences(false);
              }}
              style={{
                padding: '8px 20px',
                background: exampleType === 'trading' ? 
                  'linear-gradient(135deg, #F4511E, #FF6D00)' : 
                  'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '20px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              📈 Trading
            </button>
            <button
              onClick={() => {
                setExampleType('data');
                setShowWelcome(true);
                setHasSetInitialPreferences(false);
              }}
              style={{
                padding: '8px 20px',
                background: exampleType === 'data' ? 
                  'linear-gradient(135deg, #F4511E, #FF6D00)' : 
                  'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '20px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🔄 Data Pipeline
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Panel - Agent-Generated Branches */}
        <div style={{
          width: '450px',
          background: 'rgba(0, 0, 0, 0.2)',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '20px',
          overflowY: 'auto'
        }}>
          {showWelcome && !hasSetInitialPreferences ? (
            // Welcome Screen - User Preferences First
            <>
              <div style={{
                background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.2), rgba(69, 183, 209, 0.2))',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                border: '1px solid rgba(78, 205, 196, 0.5)',
                textAlign: 'center'
              }}>
                <h2 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>
                  👋 Welcome!
                </h2>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
                  Before we show you solutions, tell us what matters most to you.
                </p>
              </div>

              {/* Task Description */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                  📋 Your Task
                </h4>
                <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5' }}>
                  "{taskDescriptions[exampleType]}"
                </p>
              </div>

              <UserInterventionPoint 
                step="1" 
                description="Tell us your priorities before AI agents start working"
              />

              {/* Quick Persona Selection */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                  🎭 Choose Your Profile
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {userPersonas[exampleType].map((persona, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentPreferences(persona.preferences);
                        setHasSetInitialPreferences(true);
                        setShowWelcome(false);
                        setUserDecisions(prev => [...prev, {
                          type: 'persona_selection',
                          timestamp: new Date().toISOString(),
                          input: `Selected ${persona.name} profile`,
                          result: persona.preferences
                        }]);
                      }}
                      style={{
                        padding: '15px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.4)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.2)';
                      }}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                        {persona.name}
                      </div>
                      <div style={{ fontSize: '12px', opacity: 0.8 }}>
                        {persona.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Natural Language Input */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.2), rgba(255, 109, 0, 0.2))',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(244, 81, 30, 0.5)'
              }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                  💬 Or Describe Your Needs
                </h4>
                <textarea
                  value={naturalLanguageInput}
                  onChange={(e) => setNaturalLanguageInput(e.target.value)}
                  placeholder={
                    exampleType === 'ml' ? "e.g., 'I need a model that's highly accurate but also explainable to non-technical stakeholders'" :
                    exampleType === 'trading' ? "e.g., 'I want high returns but with minimal risk and fast execution'" :
                    exampleType === 'data' ? "e.g., 'I need maximum throughput but it must be reliable and cost-effective'" :
                    "e.g., 'I want fast code but it needs to be maintainable by junior developers'"
                  }
                  style={{
                    width: '100%',
                    height: '80px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '10px',
                    color: 'white',
                    fontSize: '12px',
                    resize: 'none',
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={async () => {
                    await processNaturalLanguage();
                    setShowWelcome(false);
                  }}
                  disabled={isProcessingNL || !naturalLanguageInput.trim()}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '10px',
                    background: isProcessingNL || !naturalLanguageInput.trim() 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'linear-gradient(135deg, #F4511E, #FF6D00)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: isProcessingNL || !naturalLanguageInput.trim() ? 'not-allowed' : 'pointer',
                    opacity: isProcessingNL || !naturalLanguageInput.trim() ? 0.5 : 1
                  }}
                >
                  {isProcessingNL ? 'Processing...' : 'Start Agent Competition'}
                </button>
              </div>

              {/* Skip Option */}
              <button
                onClick={() => {
                  setHasSetInitialPreferences(true);
                  setShowWelcome(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '10px',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Skip and use default preferences →
              </button>
            </>
          ) : (
            // Results Screen - Show Code Branches
            <>
              {/* Task Description */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.2), rgba(255, 109, 0, 0.2))',
                borderRadius: '12px',
                padding: '15px',
                marginBottom: '20px',
                border: '1px solid rgba(244, 81, 30, 0.5)'
              }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🎯</span> Task Given to All Agents
                </h4>
                <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5' }}>
                  "{taskDescriptions[exampleType]}"
                </p>
              </div>

              {/* Git-style Branch Visualization */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '12px',
                padding: '15px',
                marginBottom: '20px',
                fontFamily: 'monospace',
                fontSize: '12px'
              }}>
                <div style={{ marginBottom: '10px', color: '#4ECDC4' }}>
                  $ git branch -a
                </div>
                <div style={{ paddingLeft: '10px' }}>
                  <div style={{ color: '#96CEB4' }}>* main</div>
                  {sortedVariations.map((v, i) => (
                    <div key={i} style={{ color: v.color }}>
                      {'  '}origin/{v.branch}
                    </div>
                  ))}
                </div>
              </div>

              <h3 style={{ margin: '0 0 5px 0', fontSize: '18px' }}>
                🌳 Agent-Generated Code Branches
              </h3>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', opacity: 0.7 }}>
                Each AI agent created a different branch with their solution
              </p>
              
              <UserInterventionPoint 
                step="2" 
                description="Review and compare different code branches"
              />
              
              {/* Quick Preference Adjustment */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.2), rgba(255, 109, 0, 0.2))',
                borderRadius: '12px',
                padding: '15px',
                marginBottom: '15px',
                border: '1px solid rgba(244, 81, 30, 0.5)'
              }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '13px' }}>
                  🎯 Adjust Preferences On-the-Fly
                </h4>
                <textarea
                  value={naturalLanguageInput}
                  onChange={(e) => setNaturalLanguageInput(e.target.value)}
                  placeholder={
                    exampleType === 'ml' ? "e.g., 'I need high accuracy but it must be fast'" :
                    exampleType === 'trading' ? "e.g., 'minimize risk while keeping good returns'" :
                    exampleType === 'data' ? "e.g., 'maximize throughput but keep costs low'" :
                    "e.g., 'I want the fastest code possible'"
                  }
                  style={{
                    width: '100%',
                    height: '50px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    padding: '8px',
                    color: 'white',
                    fontSize: '11px',
                    resize: 'none',
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={processNaturalLanguage}
                  disabled={isProcessingNL || !naturalLanguageInput.trim()}
                  style={{
                    width: '100%',
                    padding: '6px',
                    marginTop: '8px',
                    background: isProcessingNL || !naturalLanguageInput.trim() 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'linear-gradient(135deg, #F4511E, #FF6D00)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: isProcessingNL || !naturalLanguageInput.trim() ? 'not-allowed' : 'pointer',
                    opacity: isProcessingNL || !naturalLanguageInput.trim() ? 0.5 : 1
                  }}
                >
                  {isProcessingNL ? 'Adjusting...' : 'Re-rank Branches'}
                </button>
              </div>
              
              {sortedVariations.map((variation, index) => {
                const perfData = getPerformanceRank(variation);
                
                return (
                  <div
                    key={variation.id}
                    onClick={() => {
                      setSelectedVariation(variation);
                      // Optionally switch to overview view to see details
                      if (viewMode !== 'overview' && viewMode !== 'gittree') {
                        setViewMode('overview');
                      }
                    }}
                    style={{
                      background: selectedVariation?.id === variation.id ? 
                        'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      border: variation.id === autoRecommended ? 
                        '2px solid #4ECDC4' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: '15px',
                      marginBottom: '15px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      transform: selectedVariation?.id === variation.id ? 'translateX(5px)' : 'translateX(0)',
                      boxShadow: selectedVariation?.id === variation.id ? 
                        `0 5px 20px ${variation.color}40` : 'none'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateX(3px)';
                      e.currentTarget.style.boxShadow = `0 3px 15px ${variation.color}30`;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = selectedVariation?.id === variation.id ? 
                        'translateX(5px)' : 'translateX(0)';
                      e.currentTarget.style.boxShadow = selectedVariation?.id === variation.id ? 
                        `0 5px 20px ${variation.color}40` : 'none';
                    }}
                  >
                    {/* Ranking Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      width: '40px',
                      height: '40px',
                      background: perfData.rank === 1 ? '#FFD700' : 
                                  perfData.rank === 2 ? '#C0C0C0' : 
                                  perfData.rank === 3 ? '#CD7F32' : 
                                  'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      border: '2px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      {perfData.rank === 1 ? '🥇' : 
                       perfData.rank === 2 ? '🥈' : 
                       perfData.rank === 3 ? '🥉' : 
                       perfData.rank}
                    </div>

                    {variation.id === autoRecommended && (
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        left: '10px',
                        background: '#4ECDC4',
                        color: '#0F2027',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        🏆 BEST FOR YOUR NEEDS
                      </div>
                    )}
                    
                    {/* Git Branch Info */}
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      marginBottom: '10px',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span style={{ color: variation.color }}>●</span>
                      <span>{variation.branch}</span>
                      <span style={{ opacity: 0.6 }}>|</span>
                      <span style={{ opacity: 0.8 }}>{variation.commitMessage}</span>
                    </div>
                    
                    {/* Agent Info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '10px',
                      fontSize: '12px'
                    }}>
                      <span style={{ fontSize: '16px' }}>{variation.agentIcon}</span>
                      <span style={{ opacity: 0.8 }}>Created by:</span>
                      <span style={{ fontWeight: '600', color: variation.color }}>
                        {variation.agent}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <h4 style={{ margin: 0, fontSize: '16px', flex: 1 }}>{variation.name}</h4>
                    </div>
                    
                    <p style={{ 
                      margin: '0 0 12px 0', 
                      fontSize: '12px', 
                      opacity: 0.7 
                    }}>
                      {variation.description}
                    </p>
                    
                    {/* Performance Summary */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      padding: '10px',
                      marginBottom: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontSize: '11px', opacity: 0.7 }}>Match Score</div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#4ECDC4' }}>
                          {perfData.score.toFixed(1)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', opacity: 0.7 }}>vs Best Match</div>
                        <div style={{ fontSize: '16px', fontWeight: '500' }}>
                          {perfData.percentOfBest}%
                        </div>
                      </div>
                    </div>
                    
                    {/* Score Visualization */}
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '10px'
                    }}>
                      <div style={{ 
                        fontWeight: '600', 
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        Metric Breakdown
                      </div>
                      
                      {Object.entries(variation.scores).map(([metric, value]) => (
                        <ScoreBar 
                          key={metric}
                          label={metric.charAt(0).toUpperCase() + metric.slice(1)} 
                          value={value} 
                          color={
                            metric === 'performance' || metric === 'accuracy' || metric === 'returns' || metric === 'throughput' ? '#FF6B6B' :
                            metric === 'memory' || metric === 'interpretability' || metric === 'risk' || metric === 'reliability' ? '#45B7D1' :
                            metric === 'readability' || metric === 'trainingSpeed' || metric === 'latency' || metric === 'cost' ? '#96CEB4' :
                            '#DDA0DD'
                          }
                          weight={currentPreferences[metric]}
                        />
                      ))}
                    </div>
                    
                    {/* Reasoning */}
                    <div style={{
                      fontSize: '11px',
                      opacity: 0.8,
                      lineHeight: '1.4'
                    }}>
                      <div style={{ marginBottom: '4px' }}>
                        <strong>Strategy:</strong> {variation.reasoning}
                      </div>
                      <div>
                        <strong>Trade-offs:</strong> {variation.tradeoffs}
                      </div>
                    </div>
                    
                    {/* Click to view indicator */}
                    <div style={{
                      marginTop: '10px',
                      textAlign: 'center',
                      fontSize: '10px',
                      opacity: 0.6,
                      fontStyle: 'italic'
                    }}>
                      {selectedVariation?.id === variation.id ? 
                        '✓ Currently viewing' : 
                        'Click to view details →'
                      }
                    </div>
                  </div>
                );
              })}

              {/* Reset Button */}
              <button
                onClick={() => {
                  setShowWelcome(true);
                  setHasSetInitialPreferences(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                ← Start Over with Different Preferences
              </button>
            </>
          )}
        </div>

        {/* Center Panel - Visualizations */}
        <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
          {!hasSetInitialPreferences ? (
            // Welcome message for center panel
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                textAlign: 'center',
                opacity: 0.5
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>👈</div>
                <h3 style={{ fontSize: '20px', marginBottom: '10px' }}>
                  Start by telling us your preferences
                </h3>
                <p style={{ fontSize: '14px' }}>
                  The AI agents are waiting for your input
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* View Mode Selector */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '20px'
              }}>
                {['overview', 'workflow', 'comparison', 'decision', 'gittree', 'approaches', 'example'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    style={{
                      padding: '8px 20px',
                      background: viewMode === mode ? 
                        'linear-gradient(135deg, #F4511E, #FF6D00)' : 
                        'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '20px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      textTransform: 'capitalize',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {mode === 'workflow' ? 'Decision Flow' : 
                     mode === 'gittree' ? 'Git Tree' : 
                     mode === 'approaches' ? 'Approaches' : mode}
                  </button>
                ))}
              </div>

              {/* Overview View */}
              {viewMode === 'overview' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0' }}>
                    Branch Performance Overview
                  </h3>
                  
                  {/* Bar Chart */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 5px 0', fontSize: '16px' }}>
                      How Well Each Branch Matches Your Needs
                    </h4>
                    <p style={{ margin: '0 0 15px 0', fontSize: '12px', opacity: 0.7 }}>
                      All agents attempted to solve: "{taskDescriptions[exampleType]}"
                    </p>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={sortedVariations.map(v => ({
                        name: v.name,
                        score: calculateWeightedScore(v),
                        fill: v.color,
                        agent: v.agent // Use full agent name
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="agent" stroke="white" fontSize={12} />
                        <YAxis stroke="white" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            background: 'rgba(0,0,0,0.8)', 
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '8px'
                          }}
                          content={({ active, payload }) => {
                            if (active && payload && payload[0]) {
                              const data = payload[0].payload;
                              return (
                                <div style={{ padding: '10px' }}>
                                  <p style={{ margin: '0 0 5px 0', fontWeight: '600' }}>{data.name}</p>
                                  <p style={{ margin: 0 }}>Score: {data.score.toFixed(1)}</p>
                                  <p style={{ margin: 0, fontSize: '11px', opacity: 0.8 }}>Agent: {data.agent}</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar dataKey="score" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Selected Variation Details */}
                  {selectedVariation ? (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '20px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        marginBottom: '15px'
                      }}>
                        <span style={{ fontSize: '20px' }}>{selectedVariation.agentIcon}</span>
                        <h4 style={{ margin: 0, fontSize: '16px' }}>
                          {selectedVariation.name} - Generated by {selectedVariation.agent}
                        </h4>
                      </div>
                      
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '10px',
                        borderRadius: '8px',
                        marginBottom: '15px',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                      }}>
                        <div style={{ color: '#4ECDC4' }}>$ git checkout {selectedVariation.branch}</div>
                        <div style={{ opacity: 0.7 }}>Switched to branch '{selectedVariation.branch}'</div>
                      </div>
                      
                      <pre style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        overflow: 'auto',
                        marginBottom: '15px'
                      }}>
                        <code>{selectedVariation.codeSnippet}</code>
                      </pre>
                      
                      <UserInterventionPoint 
                        step="3" 
                        description="Review implementation and decide if it meets your requirements"
                      />
                    </div>
                  ) : (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '10px', opacity: 0.5 }}>
                        👈
                      </div>
                      <p style={{ fontSize: '14px', opacity: 0.7 }}>
                        Click on any branch to view its implementation details
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Workflow View */}
              {viewMode === 'workflow' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0' }}>User-First Decision Workflow</h3>
                  
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '30px'
                  }}>
                    {/* Workflow steps */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Step 1 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          background: '#FFC107',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}>
                          1
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 5px 0' }}>User Sets Preferences</h4>
                          <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
                            You define what matters most BEFORE agents start working
                          </p>
                        </div>
                      </div>

                      <div style={{ marginLeft: '20px', borderLeft: '2px dashed rgba(255,255,255,0.3)', height: '30px' }} />

                      {/* Step 2 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          background: '#F4511E',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}>
                          2
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 5px 0' }}>Task Assignment</h4>
                          <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
                            Task: "{taskDescriptions[exampleType]}"
                          </p>
                        </div>
                      </div>

                      <div style={{ marginLeft: '20px', borderLeft: '2px dashed rgba(255,255,255,0.3)', height: '30px' }} />

                      <UserInterventionPoint 
                        step="3" 
                        description="Define what matters most to you"
                      />

                      <div style={{ marginLeft: '20px', borderLeft: '2px dashed rgba(255,255,255,0.3)', height: '30px' }} />

                      {/* Step 4 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          background: '#96CEB4',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}>
                          4
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 5px 0' }}>Ranked Results</h4>
                          <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
                            Solutions ranked by your preferences - {sortedVariations[0].agent} leads with {getPerformanceRank(sortedVariations[0]).score.toFixed(1)} points
                          </p>
                        </div>
                      </div>

                      <div style={{ marginLeft: '20px', borderLeft: '2px dashed rgba(255,255,255,0.3)', height: '30px' }} />

                      <UserInterventionPoint 
                        step="5" 
                        description="Make final decision: accept top solution, choose another, or request modifications"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Comparison View */}
              {viewMode === 'comparison' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0' }}>Multi-Metric Comparison</h3>
                  
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                      How Each Agent Balanced Different Metrics
                    </h4>
                    <ResponsiveContainer width="100%" height={400}>
                      <RadarChart data={Object.keys(currentVariations[0].scores).map(metric => {
                        const point = { metric: metric.charAt(0).toUpperCase() + metric.slice(1) };
                        currentVariations.forEach(v => {
                          point[v.name] = v.scores[metric];
                        });
                        return point;
                      })}>
                        <PolarGrid stroke="rgba(255,255,255,0.3)" />
                        <PolarAngleAxis dataKey="metric" stroke="white" fontSize={12} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="white" fontSize={10} />
                        {currentVariations.map(v => (
                          <Radar
                            key={v.id}
                            name={v.name}
                            dataKey={v.name}
                            stroke={v.color}
                            fill={v.color}
                            fillOpacity={0.3}
                            strokeWidth={2}
                          />
                        ))}
                        <Tooltip 
                          contentStyle={{ 
                            background: 'rgba(0,0,0,0.8)', 
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '8px'
                          }} 
                        />
                        <Legend 
                          wrapperStyle={{ fontSize: '12px' }}
                          verticalAlign="bottom"
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Decision History View */}
              {viewMode === 'decision' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0' }}>Decision History</h3>
                  
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                      Your Decision Points
                    </h4>
                    
                    {userDecisions.length === 0 ? (
                      <p style={{ fontSize: '14px', opacity: 0.7, textAlign: 'center', padding: '40px 0' }}>
                        No decisions made yet. Set your preferences to start.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {userDecisions.map((decision, index) => (
                          <div key={index} style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            padding: '15px',
                            borderLeft: '3px solid #FFC107'
                          }}>
                            <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '5px' }}>
                              {new Date(decision.timestamp).toLocaleString()}
                            </div>
                            <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                              <strong>Action:</strong> {decision.input}
                            </div>
                            <div style={{ fontSize: '12px' }}>
                              <strong>Result:</strong> 
                              <div style={{ marginTop: '5px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                {Object.entries(decision.result).map(([key, value]) => (
                                  <span key={key} style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '11px'
                                  }}>
                                    {key}: {(value * 100).toFixed(0)}%
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Git Tree View */}
              {viewMode === 'gittree' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0' }}>Git Branch Tree</h3>
                  
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    borderRadius: '12px',
                    padding: '30px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    overflow: 'auto'
                  }}>
                    <div style={{ marginBottom: '25px', color: '#4ECDC4', fontSize: '14px' }}>
                      $ git log --graph --pretty=format:'%h %s' --abbrev-commit --all
                    </div>
                    
                    {/* Main branch timeline */}
                    <div style={{ position: 'relative', paddingLeft: '20px' }}>
                      {/* Initial commit on main */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px', position: 'relative' }}>
                        <div style={{
                          position: 'absolute',
                          left: '-20px',
                          width: '14px',
                          height: '14px',
                          background: '#96CEB4',
                          borderRadius: '50%',
                          border: '3px solid rgba(150, 206, 180, 0.3)',
                          boxShadow: '0 0 10px rgba(150, 206, 180, 0.5)'
                        }} />
                        <span style={{ color: '#FFD700', marginRight: '10px' }}>a3f8d2c</span>
                        <span style={{ 
                          background: '#96CEB4', 
                          color: '#000', 
                          padding: '2px 8px', 
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          marginRight: '10px'
                        }}>
                          main
                        </span>
                        <span>Initial task: {taskDescriptions[exampleType].substring(0, 50)}...</span>
                      </div>
                      
                      {/* Vertical line from main */}
                      <div style={{
                        position: 'absolute',
                        left: '-13px',
                        top: '20px',
                        width: '2px',
                        height: '40px',
                        background: 'linear-gradient(to bottom, #96CEB4, rgba(150, 206, 180, 0.3))'
                      }} />
                      
                      {/* Branch point indicator */}
                      <div style={{ 
                        position: 'relative', 
                        marginBottom: '20px',
                        marginLeft: '-20px',
                        paddingLeft: '40px'
                      }}>
                        <div style={{
                          position: 'absolute',
                          left: '7px',
                          top: '10px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent)',
                          animation: 'pulse 2s infinite'
                        }} />
                        <span style={{ 
                          color: '#FFC107', 
                          fontSize: '11px',
                          background: 'rgba(255, 193, 7, 0.2)',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          display: 'inline-block'
                        }}>
                          🌿 AI agents create branches here
                        </span>
                      </div>
                      
                      {/* Agent branches */}
                      <div style={{ position: 'relative', marginTop: '30px' }}>
                        {sortedVariations.map((variation, index) => {
                          const perfData = getPerformanceRank(variation);
                          const offset = index * 80;
                          const isRecommended = perfData.rank === 1;
                          
                          return (
                            <div key={variation.id} style={{
                              position: 'relative',
                              marginBottom: '25px',
                              marginLeft: `${offset}px`,
                              transition: 'all 0.3s ease'
                            }}>
                              {/* Branch line from main */}
                              <svg style={{
                                position: 'absolute',
                                left: `-${offset + 20}px`,
                                top: '-30px',
                                width: `${offset + 40}px`,
                                height: '50px',
                                pointerEvents: 'none'
                              }}>
                                <path
                                  d={`M 10 0 Q 10 25, ${offset + 30} 45`}
                                  stroke={variation.color}
                                  strokeWidth="2"
                                  fill="none"
                                  strokeDasharray={isRecommended ? "0" : "5,5"}
                                  opacity={isRecommended ? 1 : 0.7}
                                />
                              </svg>
                              
                              {/* Commit node */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 16px',
                                background: isRecommended 
                                  ? `linear-gradient(135deg, ${variation.color}20, ${variation.color}10)`
                                  : 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '12px',
                                border: `1px solid ${isRecommended ? variation.color : 'rgba(255, 255, 255, 0.1)'}`,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.transform = 'translateX(5px)';
                                e.currentTarget.style.boxShadow = `0 5px 20px ${variation.color}40`;
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              onClick={() => setSelectedVariation(variation)}
                              >
                                {/* Commit dot */}
                                <div style={{
                                  width: '16px',
                                  height: '16px',
                                  background: variation.color,
                                  borderRadius: '50%',
                                  boxShadow: `0 0 10px ${variation.color}80`,
                                  flexShrink: 0
                                }} />
                                
                                {/* Commit info */}
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={{ color: '#FFD700', fontSize: '12px' }}>{`${variation.id}f8${variation.id}2c`}</span>
                                    <span style={{ 
                                      color: variation.color, 
                                      fontSize: '11px',
                                      background: `${variation.color}20`,
                                      padding: '2px 8px',
                                      borderRadius: '10px'
                                    }}>
                                      {variation.branch}
                                    </span>
                                    {isRecommended && (
                                      <span style={{ 
                                        background: 'linear-gradient(135deg, #4ECDC4, #45B7D1)', 
                                        color: '#fff', 
                                        padding: '2px 10px', 
                                        borderRadius: '10px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 8px rgba(78, 205, 196, 0.4)'
                                      }}>
                                        ✨ BEST MATCH
                                      </span>
                                    )}
                                  </div>
                                  
                                  <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                                    {variation.commitMessage}
                                  </div>
                                  
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', fontSize: '11px', opacity: 0.8 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      {variation.agentIcon} {variation.agent}
                                    </span>
                                    <span>•</span>
                                    <span>Score: <strong style={{ color: '#4ECDC4' }}>{perfData.score.toFixed(1)}</strong></span>
                                    <span>•</span>
                                    <span>Rank: <strong style={{ color: variation.color }}>#{perfData.rank}</strong></span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Merge visualization */}
                      <div style={{
                        marginTop: '40px',
                        paddingTop: '30px',
                        borderTop: '2px dashed rgba(255,255,255,0.1)',
                        position: 'relative'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: '-15px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#0F2027',
                          padding: '5px 15px',
                          borderRadius: '15px',
                          border: '1px solid rgba(255,255,255,0.2)',
                          fontSize: '12px',
                          color: '#FFC107'
                        }}>
                          🔀 Ready to merge
                        </div>
                        
                        <div style={{
                          background: 'rgba(78, 205, 196, 0.1)',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid rgba(78, 205, 196, 0.3)'
                        }}>
                          <h5 style={{ margin: '0 0 15px 0', color: '#4ECDC4', fontSize: '14px' }}>
                            Recommended Merge Strategy
                          </h5>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              background: 'rgba(0, 0, 0, 0.3)',
                              padding: '10px',
                              borderRadius: '6px'
                            }}>
                              <span style={{ color: '#96CEB4' }}>$</span> git checkout main<br/>
                              <span style={{ color: '#96CEB4' }}>$</span> git merge {sortedVariations[0]?.branch}
                              <span style={{ color: '#666', marginLeft: '10px' }}># Best match: {sortedVariations[0]?.name}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Add pulse animation */}
                    <style>{`
                      @keyframes pulse {
                        0% { opacity: 0.4; transform: scale(1); }
                        50% { opacity: 0.8; transform: scale(1.2); }
                        100% { opacity: 0.4; transform: scale(1); }
                      }
                    `}</style>
                  </div>
                  
                  {/* Interactive command panel */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '20px',
                    marginTop: '20px'
                  }}>
                    {/* Branch comparison */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}>
                      <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px' }}>🔍</span> Compare Branches
                      </h5>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '10px',
                        borderRadius: '6px'
                      }}>
                        $ git diff {sortedVariations[0]?.branch}..{sortedVariations[1]?.branch}
                      </div>
                    </div>
                    
                    {/* View specific branch */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}>
                      <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px' }}>👁️</span> View Changes
                      </h5>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '10px',
                        borderRadius: '6px'
                      }}>
                        $ git show {selectedVariation?.branch || sortedVariations[0]?.branch}
                      </div>
                    </div>
                  </div>
                  
                  <UserInterventionPoint 
                    step="5" 
                    description="Review branches and choose which solution to merge"
                  />
                </div>
              )}

              {/* Approaches Comparison View */}
              {viewMode === 'approaches' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0' }}>Artemis Evolution: Three Approaches</h3>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '20px',
                    marginBottom: '30px'
                  }}>
                    {/* Approach 1: Single Agent */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '20px',
                        background: '#0F2027',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#FF6B6B'
                      }}>
                        Approach 1: Basic
                      </div>
                      
                      <h4 style={{ margin: '20px 0 15px 0', fontSize: '16px' }}>
                        🤖 Agent "optimize my code"
                      </h4>
                      
                      {/* Visual diagram */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '15px',
                        margin: '20px 0',
                        minHeight: '100px'
                      }}>
                        <div style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          fontSize: '13px'
                        }}>
                          📁 Repo
                        </div>
                        <div style={{ color: '#666' }}>→</div>
                        <div style={{
                          background: 'rgba(255, 107, 107, 0.2)',
                          padding: '15px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 107, 107, 0.5)',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '13px', marginBottom: '5px' }}>Agent reads files</div>
                          <div style={{ fontSize: '11px', opacity: 0.8 }}>Single perspective</div>
                        </div>
                        <div style={{ color: '#666' }}>→</div>
                        <div style={{
                          background: 'rgba(255, 107, 107, 0.1)',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          fontSize: '13px'
                        }}>
                          🌿 1 branch
                        </div>
                      </div>
                      
                      {/* Pros/Cons */}
                      <div style={{ fontSize: '12px', marginTop: '20px' }}>
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#4ECDC4' }}>✓ Pros:</strong>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            <li>Simple & fast</li>
                            <li>Low cost</li>
                            <li>Quick iteration</li>
                          </ul>
                        </div>
                        <div>
                          <strong style={{ color: '#FF6B6B' }}>✗ Cons:</strong>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            <li>No alternatives</li>
                            <li>Limited optimization</li>
                            <li>No user preferences</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Approach 2: Current Artemis */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid rgba(78, 205, 196, 0.3)',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '20px',
                        background: '#0F2027',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#4ECDC4'
                      }}>
                        Approach 2: Current
                      </div>
                      
                      <h4 style={{ margin: '20px 0 15px 0', fontSize: '16px' }}>
                        🚀 Artemis "optimize my code"
                      </h4>
                      
                      {/* Visual diagram */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px',
                        margin: '20px 0',
                        minHeight: '100px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            padding: '10px 15px',
                            borderRadius: '8px',
                            fontSize: '13px'
                          }}>
                            📁 Repo
                          </div>
                          <div style={{ color: '#666' }}>→</div>
                          <div style={{
                            background: 'rgba(78, 205, 196, 0.2)',
                            padding: '15px',
                            borderRadius: '8px',
                            border: '1px solid rgba(78, 205, 196, 0.5)',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '13px', marginBottom: '5px' }}>Agent reads files</div>
                            <div style={{ fontSize: '11px', opacity: 0.8 }}>or scans all code</div>
                          </div>
                        </div>
                        
                        <div style={{ color: '#666' }}>↓</div>
                        
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'center' }}>
                          {['file1', 'file2', 'file3', 'file4'].map((file, i) => (
                            <div key={i} style={{
                              background: 'rgba(78, 205, 196, 0.1)',
                              padding: '5px 10px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              border: '1px solid rgba(78, 205, 196, 0.3)'
                            }}>
                              {file} → changes
                            </div>
                          ))}
                        </div>
                        
                        <div style={{ color: '#666' }}>↓</div>
                        
                        <div style={{
                          background: 'rgba(150, 206, 180, 0.2)',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          border: '1px solid rgba(150, 206, 180, 0.5)'
                        }}>
                          🧬 Evolution + User Metrics
                        </div>
                        
                        <div style={{ color: '#666' }}>↓</div>
                        
                        <div style={{
                          background: 'rgba(78, 205, 196, 0.1)',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          fontSize: '13px'
                        }}>
                          🌿 Best branch
                        </div>
                      </div>
                      
                      {/* Pros/Cons */}
                      <div style={{ fontSize: '12px', marginTop: '20px' }}>
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#4ECDC4' }}>✓ Pros:</strong>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            <li>Repository-wide vision</li>
                            <li>Evolutionary improvement</li>
                            <li>User preference control</li>
                            <li>Transparent scoring</li>
                          </ul>
                        </div>
                        <div>
                          <strong style={{ color: '#FF6B6B' }}>✗ Cons:</strong>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            <li>Single agent perspective</li>
                            <li>Longer processing</li>
                            <li>Complex setup</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Approach 3: Multi-Agent Artemis */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.1), rgba(255, 109, 0, 0.1))',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '2px solid rgba(244, 81, 30, 0.5)',
                      position: 'relative',
                      boxShadow: '0 5px 20px rgba(244, 81, 30, 0.2)'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '20px',
                        background: 'linear-gradient(135deg, #F4511E, #FF6D00)',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: 'white'
                      }}>
                        Approach 3: Future 🔥
                      </div>
                      
                      <h4 style={{ margin: '20px 0 15px 0', fontSize: '16px' }}>
                        🚀 Artemis Agent Evolution
                      </h4>
                      
                      {/* Visual diagram */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px',
                        margin: '20px 0',
                        minHeight: '100px'
                      }}>
                        <div style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          fontSize: '13px'
                        }}>
                          📁 Repo
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          gap: '20px',
                          position: 'relative'
                        }}>
                          <div style={{ color: '#666', position: 'absolute', left: '-15px', top: '50%' }}>→</div>
                          {[
                            { name: 'Agent 1', icon: '🤖', color: '#FF6B6B' },
                            { name: 'Agent 2', icon: '🧠', color: '#4ECDC4' },
                            { name: 'Agent 3', icon: '⚡', color: '#96CEB4' },
                            { name: 'Agent 4', icon: '🔮', color: '#DDA0DD' }
                          ].map((agent, i) => (
                            <div key={i} style={{ textAlign: 'center' }}>
                              <div style={{
                                background: `rgba(255, 255, 255, 0.1)`,
                                padding: '10px',
                                borderRadius: '8px',
                                border: `1px solid ${agent.color}`,
                                marginBottom: '5px'
                              }}>
                                <div style={{ fontSize: '20px' }}>{agent.icon}</div>
                                <div style={{ fontSize: '10px', marginTop: '5px' }}>{agent.name}</div>
                              </div>
                              <div style={{ color: '#666', fontSize: '10px' }}>↓</div>
                              <div style={{
                                background: `${agent.color}20`,
                                padding: '4px 8px',
                                borderRadius: '6px',
                                fontSize: '10px',
                                border: `1px solid ${agent.color}50`
                              }}>
                                branch
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div style={{ color: '#666' }}>↓</div>
                        
                        <div style={{
                          background: 'rgba(244, 81, 30, 0.2)',
                          padding: '10px 20px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          border: '1px solid rgba(244, 81, 30, 0.5)',
                          textAlign: 'center'
                        }}>
                          <div>🔄 Artemis reviews & evolves</div>
                          <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '3px' }}>
                            + User preferences
                          </div>
                        </div>
                        
                        <div style={{ color: '#666' }}>↓</div>
                        
                        <div style={{
                          background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.3), rgba(255, 109, 0, 0.3))',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          border: '1px solid rgba(244, 81, 30, 0.5)',
                          fontWeight: '600'
                        }}>
                          🏆 Best evolved branch
                        </div>
                      </div>
                      
                      {/* Pros/Cons */}
                      <div style={{ fontSize: '12px', marginTop: '20px' }}>
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#4ECDC4' }}>✓ Pros:</strong>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            <li>Maximum diversity</li>
                            <li>Best of all AI models</li>
                            <li>Competitive improvement</li>
                            <li>User control + AI insights</li>
                            <li>Reduced AI bias</li>
                          </ul>
                        </div>
                        <div>
                          <strong style={{ color: '#FF6B6B' }}>✗ Cons:</strong>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            <li>Higher cost</li>
                            <li>Longer processing</li>
                            <li>Complex coordination</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comparison Table */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '20px',
                    overflow: 'auto'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                      📊 Detailed Comparison
                    </h4>
                    
                    <table style={{ 
                      width: '100%', 
                      fontSize: '12px',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr>
                          <th style={{ 
                            textAlign: 'left', 
                            padding: '10px',
                            borderBottom: '2px solid rgba(255,255,255,0.2)',
                            fontWeight: '600'
                          }}>
                            Feature
                          </th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: '10px',
                            borderBottom: '2px solid rgba(255,255,255,0.2)',
                            color: '#FF6B6B'
                          }}>
                            Single Agent
                          </th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: '10px',
                            borderBottom: '2px solid rgba(255,255,255,0.2)',
                            color: '#4ECDC4'
                          }}>
                            Artemis Classic
                          </th>
                          <th style={{ 
                            textAlign: 'center', 
                            padding: '10px',
                            borderBottom: '2px solid rgba(255,255,255,0.2)',
                            background: 'rgba(244, 81, 30, 0.1)',
                            color: '#F4511E'
                          }}>
                            Multi-Agent Artemis
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { feature: 'Human Control', basic: '❌ Minimal', current: '✅ Good', future: '✅ Maximum' },
                          { feature: 'Solution Diversity', basic: '❌ None', current: '⚠️ Limited', future: '✅ High' },
                          { feature: 'Codebase Understanding', basic: '⚠️ Partial', current: '✅ Complete', future: '✅ Multiple views' },
                          { feature: 'Evolution/Improvement', basic: '❌ None', current: '✅ Yes', future: '✅ Advanced' },
                          { feature: 'Processing Speed', basic: '✅ Fast', current: '⚠️ Moderate', future: '❌ Slower' },
                          { feature: 'Cost', basic: '✅ Low', current: '⚠️ Medium', future: '❌ High' },
                          { feature: 'Transparency', basic: '❌ Black box', current: '✅ High', future: '✅ Maximum' },
                          { feature: 'Best For', basic: 'Quick fixes', current: 'Full optimization', future: 'Critical systems' }
                        ].map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ 
                              padding: '8px 10px',
                              borderBottom: '1px solid rgba(255,255,255,0.1)'
                            }}>
                              {row.feature}
                            </td>
                            <td style={{ 
                              padding: '8px 10px',
                              borderBottom: '1px solid rgba(255,255,255,0.1)',
                              textAlign: 'center'
                            }}>
                              {row.basic}
                            </td>
                            <td style={{ 
                              padding: '8px 10px',
                              borderBottom: '1px solid rgba(255,255,255,0.1)',
                              textAlign: 'center'
                            }}>
                              {row.current}
                            </td>
                            <td style={{ 
                              padding: '8px 10px',
                              borderBottom: '1px solid rgba(255,255,255,0.1)',
                              textAlign: 'center',
                              background: 'rgba(244, 81, 30, 0.05)'
                            }}>
                              {row.future}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Key Insight */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(69, 183, 209, 0.1))',
                    borderRadius: '12px',
                    padding: '20px',
                    marginTop: '20px',
                    border: '1px solid rgba(78, 205, 196, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#4ECDC4' }}>
                      💡 Key Insight
                    </h4>
                    <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6' }}>
                      The Multi-Agent Artemis approach treats AI agents like a diverse team of developers, 
                      each creating their own feature branch. Artemis acts as the tech lead who reviews all 
                      approaches, helps them evolve, and presents the best options based on your priorities. 
                      This maximizes both AI capability and human control.
                    </p>
                  </div>
                </div>
              )}

              {/* Example View - Human Decision Making */}
              {viewMode === 'example' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0' }}>Human Decision Making Example</h3>
                  
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ 
                      margin: '0 0 20px 0', 
                      fontSize: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span>📝</span> Task: "Optimize our authentication service for better performance"
                    </h4>

                    {/* Multiple Agent Outputs */}
                    <div style={{ marginBottom: '30px' }}>
                      <h5 style={{ 
                        fontSize: '16px', 
                        marginBottom: '15px',
                        color: '#4ECDC4'
                      }}>
                        🤖 Agent Outputs (Scored by Intelligence System)
                      </h5>
                      
                      <div style={{ 
                        display: 'grid', 
                        gap: '15px'
                      }}>
                        {[
                          {
                            agent: 'GPT Agent',
                            icon: '⚡',
                            solution: 'Implement Redis caching for session management',
                            code: `// Add Redis for session caching
const redis = new Redis();
await redis.set(sessionId, userData, 'EX', 3600);`,
                            reasoning: 'Reduces database queries by 80%, improves response time',
                            scores: { performance: 95, security: 85, maintainability: 70, cost: 60 },
                            intelligenceScore: 88,
                            color: '#FF6B6B'
                          },
                          {
                            agent: 'Claude Agent',
                            icon: '🔒',
                            solution: 'Implement JWT with refresh token rotation',
                            code: `// Enhanced JWT implementation
const token = jwt.sign(payload, secret, { expiresIn: '15m' });
const refreshToken = generateSecureToken();`,
                            reasoning: 'Better security with stateless auth, prevents token replay',
                            scores: { performance: 75, security: 98, maintainability: 85, cost: 70 },
                            intelligenceScore: 82,
                            color: '#4ECDC4'
                          },
                          {
                            agent: 'Gemini Agent',
                            icon: '🎯',
                            solution: 'Use database connection pooling with optimized queries',
                            code: `// Optimized DB pooling
const pool = createPool({ max: 20, idleTimeout: 30000 });
const result = await pool.query(optimizedQuery);`,
                            reasoning: 'Improves throughput without external dependencies',
                            scores: { performance: 85, security: 80, maintainability: 90, cost: 95 },
                            intelligenceScore: 85,
                            color: '#96CEB4'
                          },
                          {
                            agent: 'Llama Agent',
                            icon: '🏗️',
                            solution: 'Implement edge authentication with CDN',
                            code: `// Edge function for auth
export async function onRequest({ request }) {
  return authenticateAtEdge(request);
}`,
                            reasoning: 'Moves auth to edge, reduces latency globally',
                            scores: { performance: 90, security: 88, maintainability: 65, cost: 50 },
                            intelligenceScore: 78,
                            color: '#DDA0DD'
                          }
                        ].map((output, idx) => (
                          <div 
                            key={idx}
                            onClick={() => setSelectedAgentOutput(output)}
                            style={{
                              background: selectedAgentOutput?.agent === output.agent ? 
                                `linear-gradient(135deg, ${output.color}20, ${output.color}10)` : 
                                'rgba(0, 0, 0, 0.2)',
                              borderRadius: '10px',
                              padding: '20px',
                              border: selectedAgentOutput?.agent === output.agent ?
                                `2px solid ${output.color}` :
                                '1px solid rgba(255, 255, 255, 0.1)',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateX(5px)';
                              e.currentTarget.style.boxShadow = `0 5px 20px ${output.color}40`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateX(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {/* Intelligence Score Badge */}
                            <div style={{
                              position: 'absolute',
                              top: '15px',
                              right: '15px',
                              background: output.intelligenceScore >= 85 ? '#4ECDC4' : 
                                         output.intelligenceScore >= 80 ? '#FFC107' : '#FF6B6B',
                              color: '#000',
                              padding: '5px 12px',
                              borderRadius: '20px',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}>
                              Score: {output.intelligenceScore}
                            </div>

                            {/* Agent Info */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              marginBottom: '12px'
                            }}>
                              <span style={{ fontSize: '20px' }}>{output.icon}</span>
                              <span style={{ 
                                fontWeight: '600', 
                                color: output.color,
                                fontSize: '14px'
                              }}>
                                {output.agent}
                              </span>
                            </div>

                            {/* Solution */}
                            <h6 style={{ 
                              margin: '0 0 10px 0', 
                              fontSize: '15px',
                              color: 'white' 
                            }}>
                              {output.solution}
                            </h6>

                            {/* Code Preview */}
                            <pre style={{
                              background: 'rgba(0, 0, 0, 0.3)',
                              padding: '10px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              marginBottom: '10px',
                              overflow: 'hidden',
                              maxHeight: '60px'
                            }}>
                              <code>{output.code}</code>
                            </pre>

                            {/* Reasoning */}
                            <p style={{ 
                              fontSize: '12px', 
                              opacity: 0.8,
                              marginBottom: '10px' 
                            }}>
                              {output.reasoning}
                            </p>

                            {/* Metric Scores */}
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(2, 1fr)',
                              gap: '5px',
                              fontSize: '11px'
                            }}>
                              {Object.entries(output.scores).map(([metric, score]) => (
                                <div key={metric} style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  padding: '3px 0'
                                }}>
                                  <span style={{ opacity: 0.7, textTransform: 'capitalize' }}>
                                    {metric}:
                                  </span>
                                  <span style={{ 
                                    fontWeight: '500',
                                    color: score >= 90 ? '#4ECDC4' : 
                                           score >= 80 ? '#96CEB4' : 
                                           score >= 70 ? '#FFC107' : '#FF6B6B'
                                  }}>
                                    {score}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Human Decision Interface */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(69, 183, 209, 0.1))',
                      borderRadius: '12px',
                      padding: '25px',
                      border: '1px solid rgba(78, 205, 196, 0.3)'
                    }}>
                      <h5 style={{ 
                        fontSize: '16px', 
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <span>👤</span> Your Decision
                      </h5>

                      {selectedAgentOutput ? (
                        <div>
                          <div style={{ marginBottom: '20px' }}>
                            <p style={{ fontSize: '14px', marginBottom: '10px' }}>
                              You've selected: <strong style={{ color: selectedAgentOutput.color }}>
                                {selectedAgentOutput.solution}
                              </strong>
                            </p>
                            <p style={{ fontSize: '12px', opacity: 0.8 }}>
                              Intelligence Score: {selectedAgentOutput.intelligenceScore}/100
                            </p>
                          </div>

                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ 
                              display: 'block', 
                              marginBottom: '10px',
                              fontSize: '14px' 
                            }}>
                              Decision Rationale:
                            </label>
                            <textarea
                              value={humanDecision}
                              onChange={(e) => setHumanDecision(e.target.value)}
                              placeholder="Why did you choose this solution? Any modifications needed?"
                              style={{
                                width: '100%',
                                height: '80px',
                                background: 'rgba(0, 0, 0, 0.3)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '8px',
                                color: 'white',
                                padding: '10px',
                                fontSize: '13px',
                                resize: 'vertical'
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              onClick={() => {
                                alert(`Decision recorded! Implementing: ${selectedAgentOutput.solution}`);
                                setHumanDecision('');
                              }}
                              style={{
                                flex: 1,
                                padding: '12px',
                                background: 'linear-gradient(135deg, #4ECDC4, #45B7D1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              ✅ Approve & Implement
                            </button>
                            <button
                              onClick={() => {
                                setSelectedAgentOutput(null);
                                setHumanDecision('');
                              }}
                              style={{
                                padding: '12px 20px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              ↩️ Change Selection
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <p style={{ fontSize: '14px', opacity: 0.7 }}>
                            👆 Click on any agent output above to make your decision
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Intelligence System Explanation */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '20px',
                      marginTop: '20px',
                      fontSize: '13px'
                    }}>
                      <h6 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#FFC107' }}>
                        🧠 How the Intelligence System Scores
                      </h6>
                      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6' }}>
                        <li>Evaluates code quality, performance impact, and security implications</li>
                        <li>Considers maintainability and operational complexity</li>
                        <li>Analyzes cost-benefit ratio and scalability</li>
                        <li>Provides an objective score to help human decision makers</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right Panel - Preferences (hidden initially) */}
        {hasSetInitialPreferences && (
          <div style={{
            width: '350px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '20px',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>
              Fine-tune Preferences
            </h3>
            
            <UserInterventionPoint 
              step="4" 
              description="Adjust weights to see rankings change"
            />
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '14px' }}>
                Current Weight Distribution
              </h4>
              
              {Object.entries(currentPreferences).map(([key, value]) => (
                <div key={key} style={{ marginBottom: '15px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '5px',
                    fontSize: '13px'
                  }}>
                    <span style={{ textTransform: 'capitalize' }}>
                      {key === 'inferenceSpeed' ? 'Inference Speed' : 
                       key === 'trainingSpeed' ? 'Training Speed' : 
                       key.charAt(0).toUpperCase() + key.slice(1)}
                    </span>
                    <span>{(value * 100).toFixed(0)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={value * 100}
                    onChange={(e) => {
                      const newValue = parseFloat(e.target.value) / 100;
                      const otherKeys = Object.keys(currentPreferences).filter(k => k !== key);
                      const remainingWeight = 1 - newValue;
                      const otherWeightSum = otherKeys.reduce((sum, k) => sum + currentPreferences[k], 0);
                      
                      const newPreferences = { ...currentPreferences, [key]: newValue };
                      
                      // Redistribute remaining weight proportionally
                      if (otherWeightSum > 0) {
                        otherKeys.forEach(k => {
                          newPreferences[k] = (currentPreferences[k] / otherWeightSum) * remainingWeight;
                        });
                      }
                      
                      setCurrentPreferences(newPreferences);
                      
                      // Add to decision history
                      setUserDecisions(prev => [...prev, {
                        type: 'manual_adjustment',
                        timestamp: new Date().toISOString(),
                        input: `Adjusted ${key} to ${(newValue * 100).toFixed(0)}%`,
                        result: newPreferences
                      }]);
                    }}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      outline: 'none',
                      WebkitAppearance: 'none'
                    }}
                  />
                </div>
              ))}
            </div>

            {/* AI Insights */}
            <div style={{
              background: 'rgba(78, 205, 196, 0.1)',
              borderRadius: '12px',
              padding: '20px',
              marginTop: '20px',
              border: '1px solid rgba(78, 205, 196, 0.5)'
            }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                🤖 AI Recommendation
              </h4>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', lineHeight: '1.5' }}>
                Based on your preferences, <strong>{sortedVariations[0].agent}</strong>'s branch 
                "<strong>{sortedVariations[0].branch}</strong>" is the best match.
              </p>
              <p style={{ margin: 0, fontSize: '11px', opacity: 0.8, lineHeight: '1.5' }}>
                This solution excels in {
                  Object.entries(currentPreferences)
                    .sort(([,a], [,b]) => b - a)[0][0]
                } (your highest priority) achieving {
                  sortedVariations[0].scores[Object.entries(currentPreferences)
                    .sort(([,a], [,b]) => b - a)[0][0]]
                }% in this metric.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtemisDecisionPlatform; 